import { PropsWithChildren } from "react";

import { SplitClient as BitSplitClient } from "@moodys/mdc-frontend.services.split";
import { Box, CssBaseline } from "@mui/material";

import { MoodysThemeProvider, split, withNfe } from "@moodys/federated";
import { config } from "config";
import { useInjectAdobeAnalytics } from "hooks/useInjectAdobeAnalytics";
import { ANONYMOUS_DEFAULT, useUserInfo } from "hooks/useUserInfo/useUserInfo";

import { Footer } from "../Footer";
import { GlobalModals } from "../GlobalModals";
import { Header } from "../Header";
import { PageMetadata } from "../PageMetadata";
import { SplitFactoryLayoutContext } from "../SplitFactoryLayoutContext/SplitFactoryLayoutContext";

import { layoutStyles as styles } from "./Layout.styles";
import { MainContent } from "./components/MainContent";

interface SplitWithUserContextProps extends PropsWithChildren {
  userKey: string;
}

const SplitWithUserContext = ({
  children,
  userKey,
}: SplitWithUserContextProps) => {
  return (
    // Necessary for squads still using Split from Bit
    // BitSplitClient will be removed once all squads are migrated to NFE Split [MDCPT-52817]
    <BitSplitClient splitKey={userKey}>
      <split.SplitClient splitKey={userKey}>{children}</split.SplitClient>
    </BitSplitClient>
  );
};

export const Layout = ({ children }: PropsWithChildren) => {
  useInjectAdobeAnalytics(config.ADOBE_DTM_URL);
  const { userEmail } = useUserInfo();

  return (
    <>
      <PageMetadata />
      <SplitWithUserContext userKey={userEmail || ANONYMOUS_DEFAULT}>
        <Box css={styles.layout} data-testid="web-core-layout">
          <Header />
          <MainContent>{children}</MainContent>
        </Box>
        <Footer />
      </SplitWithUserContext>
    </>
  );
};

export const LayoutWrapper = withNfe(({ children }: PropsWithChildren) => {
  return (
    <SplitFactoryLayoutContext>
      <MoodysThemeProvider>
        <CssBaseline />
        <GlobalModals />
        <Layout>{children}</Layout>
      </MoodysThemeProvider>
    </SplitFactoryLayoutContext>
  );
});
